'use client'

import React, { type ComponentType } from 'react'
import {
  ArrowRight,
  CalendarCheck,
  ClipboardList,
  Phone,
  RotateCcw,
  ShieldCheck,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import WhatsApp from '@/components/whatsapp'
import { useWhatsApp } from '@/config/flags'
import { trackAnalyticsEvent } from '@/lib/analytics'
import { clinicIdentity } from '@/lib/clinic-identity'

interface BookingOptionsProps {
  onOpenModal: () => void
  hasBookingDraft?: boolean
  onContinueBooking?: () => void
  onStartNewBooking?: () => void
}

type BookingTileProps = {
  Icon: ComponentType<{ className?: string }>
  title: string
  description: string
  buttonText: string
  onClick: () => void
  color: 'green' | 'blue' | 'purple'
  /**
   * Semantic destination (tel:/wa.me). When set the tile renders a real
   * anchor so the contact action works without JS and is crawlable;
   * onClick still fires for analytics.
   */
  href?: string
  external?: boolean
}

const tileColors = {
  green: {
    border: 'hover:border-green-500',
    iconBg: 'bg-green-100',
    icon: 'text-green-600',
    button: 'bg-green-600 hover:bg-green-700',
  },
  blue: {
    border: 'hover:border-blue-500',
    iconBg: 'bg-blue-100',
    icon: 'text-blue-600',
    button: 'bg-blue-600 hover:bg-blue-700',
  },
  purple: {
    border: 'hover:border-purple-500',
    iconBg: 'bg-purple-100',
    icon: 'text-purple-600',
    button: 'bg-purple-600 hover:bg-purple-700',
  },
} as const

const BookingTile: React.FC<BookingTileProps> = ({
  Icon,
  title,
  description,
  buttonText,
  onClick,
  color,
  href,
  external,
}) => {
  const colors = tileColors[color]

  return (
    <Card
      className={`h-full border-2 p-4 transition-all duration-200 sm:p-6 ${colors.border}`}
    >
      <CardContent className="flex h-full flex-col items-center p-0 text-center">
        <div
          className={`mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full sm:mb-4 sm:h-16 sm:w-16 ${colors.iconBg}`}
        >
          <Icon className={`h-6 w-6 sm:h-8 sm:w-8 ${colors.icon}`} />
        </div>

        <h3 className="mb-1 text-base font-semibold sm:mb-2 sm:text-lg">
          {title}
        </h3>

        <p className="mb-4 text-xs leading-relaxed text-gray-600 sm:text-sm">
          {description}
        </p>

        {href ? (
          <Button
            asChild
            className={`mt-auto w-full text-sm text-white sm:text-base ${colors.button}`}
          >
            <a
              href={href}
              {...(external
                ? { target: '_blank', rel: 'noopener noreferrer' }
                : {})}
              onClick={onClick}
            >
              {buttonText}
            </a>
          </Button>
        ) : (
          <Button
            type="button"
            className={`mt-auto w-full text-sm text-white sm:text-base ${colors.button}`}
            onClick={onClick}
          >
            {buttonText}
          </Button>
        )}
      </CardContent>
    </Card>
  )
}

const BookingOptions: React.FC<BookingOptionsProps> = ({
  onOpenModal,
  hasBookingDraft = false,
  onContinueBooking,
  onStartNewBooking,
}) => {
  const whatsappEnabled = useWhatsApp

  const tiles: BookingTileProps[] = [
    {
      Icon: CalendarCheck,
      title: 'Book online',
      description:
        'Verify your phone number, choose the patient, then pick the right appointment slot.',
      buttonText: 'Start Online Booking',
      color: 'purple',
      onClick: onOpenModal,
    },
    {
      Icon: WhatsApp,
      title: 'Book on WhatsApp',
      description: 'Quick booking with a simple chat.',
      buttonText: 'Chat on WhatsApp',
      color: 'green',
      // Real anchor (crawlable, works without JS); onClick only tracks.
      href: `${clinicIdentity.contact.whatsapp.href}?text=${encodeURIComponent(
        'Hi, I would like to book an appointment at ReACH.',
      )}`,
      external: true,
      onClick: () => {
        trackAnalyticsEvent('whatsapp_click', {
          cta_location: 'booking_options',
        })
      },
    },
    {
      Icon: Phone,
      title: 'Call to book',
      description: 'Speak directly with our booking team.',
      buttonText: 'Call Now',
      color: 'blue',
      href: clinicIdentity.contact.phone.href,
      onClick: () => {
        trackAnalyticsEvent('phone_click', { cta_location: 'booking_options' })
      },
    },
  ]

  const confirmationText = whatsappEnabled
    ? 'You’ll see confirmation on screen and receive appointment updates on WhatsApp.'
    : 'You’ll see confirmation on screen after booking.'

  const steps = [
    {
      Icon: ShieldCheck,
      title: 'Verify your phone number',
      text: 'Start with your mobile number so we can securely find your ReACH profile.',
    },
    {
      Icon: ClipboardList,
      title: 'Choose patient and care type',
      text: 'Select the patient profile, then choose an Online or In-person consultation.',
    },
    {
      Icon: CalendarCheck,
      title: 'Pick clinician and slot',
      text: 'Existing patients may get their preferred clinician pre-selected. You can still change the clinician before choosing a slot.',
    },
    {
      Icon: ShieldCheck,
      title: 'Review and confirm',
      text: `Review the appointment summary. If consent is pending, we’ll show the form only before final confirmation. ${confirmationText}`,
    },
  ]

  return (
    <section className="py-10 sm:py-12 md:py-16">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {hasBookingDraft && (
          <div className="mb-6 rounded-2xl border border-blue-100 bg-blue-50/80 p-4 text-center shadow-sm sm:mb-8 sm:p-5">
            <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-blue-100">
              <RotateCcw className="h-5 w-5 text-blue-700" />
            </div>

            <h2 className="mt-3 text-base font-semibold text-blue-950 sm:text-lg">
              Continue your booking
            </h2>

            <p className="mx-auto mt-1 max-w-xl text-xs leading-relaxed text-blue-800 sm:text-sm">
              You have an unfinished online booking. Continue from where you
              left off if this page is still open, or verify again if the page
              was refreshed.
            </p>

            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-center">
              <Button
                type="button"
                onClick={onContinueBooking ?? onOpenModal}
                className="rounded-full bg-blue-600 px-6 text-white hover:bg-blue-700"
              >
                Continue booking
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={onStartNewBooking ?? onOpenModal}
                className="rounded-full border-blue-200 bg-white text-blue-700 hover:bg-blue-50"
              >
                Start new booking
              </Button>
            </div>

            <p className="mt-3 text-[11px] leading-relaxed text-blue-700/80 sm:text-xs">
              For privacy, ReACH does not save patient details in browser
              storage.
            </p>
          </div>
        )}

        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 md:grid-cols-3">
          {tiles.map((tile) => (
            <BookingTile key={tile.title} {...tile} />
          ))}
        </div>

        <div className="mb-8 flex items-center justify-center px-2 sm:mb-12 sm:px-0">
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-center sm:px-4 sm:py-3">
            <p className="text-xs text-red-700 sm:text-sm">
              Please reach out to us{' '}
              <span className="font-semibold">
                {clinicIdentity.hours.display}
              </span>{' '}
              for appointments or queries. Sunday: {clinicIdentity.hours.sunday}
              .
            </p>
          </div>
        </div>

        <Card className="p-5 sm:p-6 md:p-8">
          <CardContent className="flex flex-col items-center p-0">
            <h2 className="mb-4 text-center text-xl font-bold sm:mb-6 sm:text-2xl">
              How online booking works
            </h2>

            <div className="flex w-full flex-col gap-3 sm:gap-4">
              {steps.map((step, index) => {
                const Icon = step.Icon

                return (
                  <div
                    key={step.title}
                    className="flex items-start gap-3 sm:gap-4"
                  >
                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-100 sm:h-9 sm:w-9">
                      <Icon className="h-4 w-4 text-green-600 sm:h-5 sm:w-5" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-green-700">
                          Step {index + 1}
                        </span>
                      </div>

                      <h3 className="mb-0.5 text-sm font-semibold sm:mb-1 sm:text-base">
                        {step.title}
                      </h3>

                      <p className="text-xs leading-relaxed text-gray-600 sm:text-sm">
                        {step.text}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="mt-6 flex w-full justify-center sm:mt-8">
              <Button
                type="button"
                size="lg"
                className="w-full bg-green-600 text-sm text-white hover:bg-green-700 sm:w-auto sm:text-base"
                onClick={onOpenModal}
              >
                Start Booking
              </Button>
            </div>

            <p className="mt-4 max-w-2xl text-center text-xs leading-relaxed text-gray-500 sm:text-sm">
              To discuss an assessment, book a consultation or contact the
              clinic.
            </p>
          </CardContent>
        </Card>
      </div>
    </section>
  )
}

export default BookingOptions
